import React from 'react';
import {List, Map} from 'immutable';
import classnames from 'classnames';
import {Link} from 'react-router';
import {callAction, callTargetedAction} from 'client/framework';
import assignmentOverviewTableStore from './AssignmentOverviewTable.store';
import assignmentOverviewCompletionStore from './AssignmentOverviewCompletion.store';
import assignmentOverviewGradeStore from './AssignmentOverviewGrade.store';
import assignmentOverviewActions from './AssignmentOverview.actions';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import Tooltip from 'client/styleguide/Tooltip/Tooltip.react';
import tooltipActions from 'client/styleguide/Tooltip/Tooltip.actions';
import IconTooltip from 'client/styleguide/IconTooltip/IconTooltip.react';
import Pagination from 'client/generic/PaginationV2/Pagination.react';
import MasteryFlag from 'generic/MasteryFlag/MasteryFlag.react';
import {stats} from 'lib/StatsUtil';
import {renderMoment} from 'lib/timeUtils';
import AssignmentCompletionAccuracy from './AssignmentCompletionAccuracy/AssignmentCompletionAccuracy.react';
import AssignmentCompletionStatus from 'client/Classrooms/shared/AssignmentCompletionStatus/AssignmentCompletionStatus.react';
import AssignmentGradeChart from './AssignmentGradeChart/AssignmentGradeChart.react';
import appStore from 'client/AppStore';
import {
  SubmissionIndicator,
  SubmissionResetModal
} from 'client/Classrooms/shared/SubmissionIndicator/SubmissionIndicator.react';
import {invalidateInterest} from 'resources/mandark.resource';
import assignmentSVG from 'client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/images/assignment.svg';

import './assignment-overview.scss';
import 'assets/stylesheets/styleguide/table.scss';

const RESET_MODAL_STORE_NAME = 'AssignmentOverviewResetModalStore';
const RESET_MODAL_WRAPPER_STORE_NAME = 'AssignmentOverviewResetModalWrapperStore';

export default class AssignmentOverview extends React.Component {
  makeSortableHeader = (headerConfig, key) => {
    const hasSortApplied =
      assignmentOverviewTableStore.getSortField() === headerConfig.get('sortFieldName');
    const sortIconType = headerConfig.get('iconType');
    const sortDirection = assignmentOverviewTableStore.getSortDirection();

    const sortClass = classnames('fa assignment-overview-table-sort-control', {
      [`fa-sort-${sortIconType}-desc`]: hasSortApplied && sortDirection === 'desc',
      [`fa-sort-${sortIconType}-asc`]: hasSortApplied && sortDirection === 'asc',
      'fa-sort': !hasSortApplied
    });

    return (
      <th key={key}>
        {headerConfig.get('name')}
        <span
          className={sortClass}
          onClick={() => {
            this.applySort(headerConfig.get('sortFieldName'));
          }}
        />
      </th>
    );
  };

  makeHelpHeader = (headerConfig, key) => {
    return (
      <th key={key}>
        {headerConfig.get('name')}
        <IconTooltip position='bottom' content={[headerConfig.get('helpContents')]} />
      </th>
    );
  };

  makeHeader = (headerConfig, key) => {
    if (headerConfig.get('isSortable')) {
      return this.makeSortableHeader(headerConfig, key);
    } else if (headerConfig.get('showHelp')) {
      return this.makeHelpHeader(headerConfig, key);
    }

    return <th key={key}>{headerConfig.get('name')}</th>;
  };

  toggleCompletionTooltip(storeName) {
    callTargetedAction({
      name: tooltipActions.TOGGLE_VISIBILITY,
      targetStore: storeName
    });
  }

  tableColumns = new List([
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Due Date',
            isSortable: true,
            sortFieldName: assignmentOverviewTableStore.getDueDateSortField(),
            iconType: 'numeric'
          }),
          key
        );
      },
      td: (assignment, key) => {
        const lateSubmissionIcon = assignment.isAllowLateSubmissions() ? (
          <IconTooltip
            icon='fa-exclamation-triangle'
            className='assignment-overview-table-late-submission-icon'
            content={['Late submissions allowed']}
          />
        ) : null;

        return (
          <td key={key}>
            <div className='assignment-overview-table-body-cell__due-date'>
              {renderMoment(assignment.getDueDate(), 'date')}
              {lateSubmissionIcon}
            </div>
            <div className='assignment-overview-table-body-cell__due-date--time'>
              {renderMoment(assignment.getDueDate(), 'time')}
            </div>
          </td>
        );
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Assignment',
            isSortable: true,
            sortFieldName: assignmentOverviewTableStore.getNameSortField(),
            iconType: 'alpha'
          }),
          key
        );
      },
      td: (assignment, key) => {
        return (
          <td key={key} className='assignment-overview-table-name-cell'>
            <Link
              className='assignment-overview-table-name-cell__link'
              to={`/classes/${
                appStore.routerProps.params.classId
              }/assignments/${assignment.getId()}/student-detail/${
                appStore.routerProps.params.userId
              }`}
            >
              {assignment.getName()}
            </Link>
          </td>
        );
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Submission Status',
            isSortable: true,
            sortFieldName: assignmentOverviewTableStore.getSubmissionStatusSortField(),
            iconType: 'amount'
          }),
          key
        );
      },
      td: (assignment, key) => {
        const isAssignmentScheduled = assignment.isScheduled();
        if (isAssignmentScheduled) {
          return (
            <td className='assignment-overview-table-submission-status assignment-overview-table-submission-status--scheduled'>
              <div>Scheduled</div>
              <div className='assignment-overview-table-submission-status__date'>
                for {assignment.getStartDate().format('MM/DD/YYYY')}
              </div>
            </td>
          );
        }
        const assignmentId = assignment.getId();
        const student = assignment.getStudents().first();
        const studentId = student.getId();
        const submittedTimestamp = student.getAssignmentRelationships(assignmentId).getSubmitted();
        const tooltipStoreName = `SubmittedStatusStore--${studentId}--${assignmentId}`;
        const isComplete = assignment.isComplete();

        let submittedColumnConfig;
        if (!submittedTimestamp) {
          submittedColumnConfig = {
            submittedString: 'Not Submitted',
            submittedTdClass: 'assignment-overview-table-submission-status--not-submitted'
          };
        } else if (assignment.isStudentSubmissionOnTime(studentId)) {
          submittedColumnConfig = {
            submittedString: 'Submitted',
            submittedTdClass: 'assignment-overview-table-submission-status--submitted'
          };
        } else if (assignment.isStudentSubmissionLate(studentId)) {
          submittedColumnConfig = {
            submittedString: 'Submitted Late',
            submittedTdClass: 'assignment-overview-table-submission-status--submitted-late'
          };
        }

        return (
          <td
            key={key}
            className={`assignment-overview-table-submission-status ${submittedColumnConfig.submittedTdClass}`}
          >
            <SubmissionIndicator
              assignment={assignment}
              isComplete={isComplete}
              student={student}
              submissionResetModalStoreName={RESET_MODAL_WRAPPER_STORE_NAME}
              submittedTimestamp={submittedTimestamp}
              tooltipStoreName={tooltipStoreName}
              windowConfirmStoreName={RESET_MODAL_STORE_NAME}
            />
          </td>
        );
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Time Spent'
          }),
          key
        );
      },
      td: (assignment, key) => {
        if (assignment.isScheduled()) {
          return <td key={key}>-</td>;
        }
        return <td key={key}>{assignment.getStudentTimeSpentMinutes()}</td>;
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Completion'
          }),
          key
        );
      },
      td: (assignment, key) => {
        const isAssignmentScheduled = assignment.isScheduled();
        if (isAssignmentScheduled) {
          return <td key={key}>-</td>;
        }
        const studentGuesses = assignment.getMeta().getStudentCountOfGuesses();
        const correctGuesses = assignment.getMeta().getStudentCountOfCorrectGuesses();
        const incorrectGuesses = studentGuesses - correctGuesses;
        const assignmentTotalQuestions = assignment.getMeta().getCountOfQuestions();

        const tooltipStoreName = `assignment-completion-tooltip-${assignment.getId()}`;
        const tooltipContents = (
          <div className='assignment-overview-table-completion-tooltip'>
            <h4 className='assignment-overview-table-completion-tooltip__label'>Total Answered</h4>
            <p className='assignment-overview-table-completion-tooltip__value'>{`(${studentGuesses}/${assignmentTotalQuestions}) - ${stats.percentage(
              studentGuesses,
              assignmentTotalQuestions,
              {fixed: true}
            )}%`}</p>
            <h4 className='assignment-overview-table-completion-tooltip__label'>Total Correct</h4>
            <p className='assignment-overview-table-completion-tooltip__value'>{`(${correctGuesses}/${assignmentTotalQuestions}) - ${stats.percentage(
              correctGuesses,
              assignmentTotalQuestions,
              {fixed: true}
            )}%`}</p>
          </div>
        );

        // We can only say this because there are not multiple guesses in the context of assignments
        const studentUnansweredQuestions = assignmentTotalQuestions - studentGuesses;
        return (
          <td key={key}>
            <div
              onClick={() => {
                this.toggleCompletionTooltip(tooltipStoreName);
              }}
              className='assignment-overview-table-completion-inline-graph'
            >
              <AssignmentCompletionAccuracy
                onClick={() => {
                  this.toggleCompletionTooltip(tooltipStoreName);
                }}
                numberCorrect={correctGuesses}
                numberIncorrect={incorrectGuesses}
                numberUnanswered={studentUnansweredQuestions}
              />
              <Tooltip position='top' color='dark' storeName={tooltipStoreName}>
                {tooltipContents}
              </Tooltip>
            </div>
          </td>
        );
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Grade',
            showHelp: true,
            helpContents: `The grade is calculated by dividing the number of questions correct by the total number of questions in the assignment.`
          }),
          key
        );
      },
      td: (assignment, key) => {
        const isAssignmentScheduled = assignment.isScheduled();
        if (isAssignmentScheduled) {
          return <td key={key}>-</td>;
        }
        const grade = stats.percentage(
          assignment.getMeta().getStudentCountOfCorrectGuesses(),
          assignment.getMeta().getCountOfQuestions(),
          {fixed: true}
        );
        return <td key={key}>{`${grade}%`}</td>;
      }
    },
    {
      th: (key) => {
        return this.makeHeader(
          new Map({
            name: 'Mastery',
            showHelp: true,
            helpContents: `Mastery is based on a student's accuracy over only the questions they attempted.`
          }),
          key
        );
      },
      td: (assignment, key) => {
        const isAssignmentScheduled = assignment.isScheduled();
        if (isAssignmentScheduled) {
          return <td key={key}>-</td>;
        }
        const accuracy = stats.percentage(
          assignment.getMeta().getStudentCountOfCorrectGuesses(),
          assignment.getMeta().getStudentCountOfGuesses(),
          {fixed: true}
        );
        let masteryFlagHasStarted = true;
        if (
          !assignment.getMeta().isStudentSubmitted() &&
          assignment.getMeta().getStudentCountOfGuesses() === 0
        ) {
          masteryFlagHasStarted = false;
        }
        return (
          <td key={key}>
            <MasteryFlag accuracy={accuracy} hasStarted={masteryFlagHasStarted} />
          </td>
        );
      }
    }
  ]);

  getAssignmentCompletionChart() {
    let contents;
    if (!assignmentOverviewCompletionStore.hasAssignmentData()) {
      contents = <LoadingIndicator />;
    } else if (assignmentOverviewCompletionStore.getAssignmentData().isEmpty()) {
      contents = (
        <div className='assignment-overview__completion-chart-message'>No assignments</div>
      );
    } else {
      contents = (
        <AssignmentCompletionStatus
          graphDescription={`${assignmentOverviewCompletionStore.getTotalAssignments()} assignments`}
          tooltipText={`${assignmentOverviewCompletionStore.getSubmittedNumber()}/${assignmentOverviewCompletionStore.getTotalAssignments()} submitted on time`}
          numberSubmitted={assignmentOverviewCompletionStore.getSubmittedNumber()}
          numberSubmittedLate={assignmentOverviewCompletionStore.getLateSubmittedNumber()}
          numberNotSubmitted={assignmentOverviewCompletionStore.getNotSubmittedNumber()}
        />
      );
    }

    return (
      <div className='assignment-overview-stats--item assignment-overview__completion-chart'>
        <h5>Submission History</h5>
        {contents}
      </div>
    );
  }

  getAssignmentOverviewChart() {
    let contents;
    if (!assignmentOverviewGradeStore.hasAssignmentData()) {
      contents = <LoadingIndicator />;
    } else if (assignmentOverviewGradeStore.getAssignmentData().isEmpty()) {
      contents = (
        <div className='assignment-overview__pie-chart-message'>No submitted assignments</div>
      );
    } else {
      contents = (
        <div className='assignment-overview__pie-chart-actual'>
          <AssignmentGradeChart
            size={100}
            strokeWidth={14}
            correctAverage={assignmentOverviewGradeStore.getCorrectAverage()}
            incorrectAverage={assignmentOverviewGradeStore.getIncorrectAverage()}
            notAnsweredAverage={assignmentOverviewGradeStore.getNotAnsweredAverage()}
          />
        </div>
      );
    }

    return (
      <div className='assignment-overview-stats--item assignment-overview__pie-chart-wrapper'>
        <div className='assignment-overview__pie-chart'>
          <h5 className='assignment-overview__pie-chart-header'>
            Average grade on submitted assignments
          </h5>
          {contents}
        </div>
      </div>
    );
  }

  applySort(sortField) {
    callAction(assignmentOverviewActions.APPLY_SORT, sortField);
  }

  /**
   * Assignment table loops through two sets of configs -- both describing how a "column" should be created.
   *
   * For each item in the `columnConfig` map, we have a function that will generate a TH cell, and a TD cell.
   * These are used together to generate both the header TR and the body TR appropriately.
   *
   */
  getAssignmentTable() {
    if (!assignmentOverviewTableStore.hasAssignmentData()) {
      return <LoadingIndicator />;
    }

    const assignmentList = assignmentOverviewTableStore.getAssignmentData();
    return (
      <table className='table table--zebra'>
        <thead>
          <tr>
            {this.tableColumns.map((columnConfig, key) => {
              const headerBuilder = columnConfig.th;
              return headerBuilder(`th-${key}`);
            })}
          </tr>
        </thead>
        <tbody>
          {assignmentList.map((assignment) => {
            return (
              <tr key={assignment.getId()}>
                {this.tableColumns.map((columnConfig, key) => {
                  const columnBuilder = columnConfig.td;
                  return columnBuilder(assignment, `td-${key}`);
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  getTablePaginationControls() {
    return assignmentOverviewTableStore.isPaginated() ? (
      <div className='assignment-overview-table__paginator'>
        <Pagination
          storeName={assignmentOverviewTableStore.getPaginatorStore().getName()}
          totalPages={assignmentOverviewTableStore.getTotalPages()}
        />
      </div>
    ) : null;
  }

  getNoAssignmentMessage() {
    return assignmentOverviewTableStore.getAssignmentData().isEmpty() ? (
      <div className='assignment-overview-no-assignments'>
        <img alt='' className='assignment-overview-no-assignments__img' src={assignmentSVG} />
        <div className='assignment-overview-no-assignments__text-wrapper'>
          <p className='assignment-overview-no-assignments__title'>No assignments found</p>
        </div>
      </div>
    ) : null;
  }

  getTableFooter() {
    if (!assignmentOverviewTableStore.hasAssignmentData()) {
      return null;
    }

    return (
      <div>
        {this.getTablePaginationControls()}
        {this.getNoAssignmentMessage()}
      </div>
    );
  }

  onSubmissionReset() {
    invalidateInterest(assignmentOverviewCompletionStore.getAssignmentQuery());
    invalidateInterest(assignmentOverviewGradeStore.getAssignmentQuery());
  }

  render() {
    // The table, when it has no results will only render its headers -- this is what we want. We can replace
    // the pagination controls with a message saying there are no assignments very simply.
    return (
      <div className='assignment-overview'>
        <div className='assignment-overview-stats'>
          {this.getAssignmentCompletionChart()}
          {this.getAssignmentOverviewChart()}
        </div>
        <div className='assignment-overview-table'>
          {this.getAssignmentTable()}
          {this.getTableFooter()}
        </div>
        <SubmissionResetModal
          onReset={this.onSubmissionReset}
          storeName={RESET_MODAL_WRAPPER_STORE_NAME}
          windowConfirmStoreName={RESET_MODAL_STORE_NAME}
        />
      </div>
    );
  }
}
